import { FormEvent, useEffect, useState } from "react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

interface NewsletterSignupProps {
  source?: string;
  variant?: "dark" | "light";
}
const TURNSTILE_SCRIPT_ID = "cf-turnstile-script";

const toSpanishError = (err: unknown) => {
  const message = err instanceof Error ? err.message : String(err || "");
  const lower = message.toLowerCase();
  if (lower.includes("edge function returned a non-2xx")) {
    return "No se pudo completar la suscripción ahora mismo. Prueba de nuevo en unos segundos.";
  }
  if (lower.includes("failed to fetch") || lower.includes("network")) {
    return "No se pudo conectar con el servidor. Revisa tu conexión e inténtalo de nuevo.";
  }
  if (lower.includes("tiempo de espera")) return "La solicitud tardó demasiado. Prueba de nuevo.";
  return "No se pudo completar la suscripción. Inténtalo de nuevo.";
};

const NewsletterSignup = ({ source = "site", variant = "dark" }: NewsletterSignupProps) => {
  const turnstileSiteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined;
  const FUNCTION_TIMEOUT_MS = 15000;
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [turnstileReady, setTurnstileReady] = useState(false);
  const [turnstileWidgetId, setTurnstileWidgetId] = useState<string | null>(null);
  const turnstileContainerId = "turnstile-newsletter-container";

  useEffect(() => {
    if (!turnstileSiteKey) return;
    const renderInvisibleWidget = () => {
      const api = (window as any).turnstile;
      const container = document.getElementById(turnstileContainerId);
      if (!api || !container || container.childElementCount > 0) return;
      const widgetId = api.render(`#${turnstileContainerId}`, {
        sitekey: turnstileSiteKey,
        size: "normal",
        execution: "execute",
      });
      setTurnstileWidgetId(widgetId ?? null);
      setTurnstileReady(true);
    };

    const existingScript = document.getElementById(TURNSTILE_SCRIPT_ID) as HTMLScriptElement | null;
    if (!existingScript) {
      const script = document.createElement("script");
      script.id = TURNSTILE_SCRIPT_ID;
      script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js";
      script.async = true;
      script.defer = true;
      script.onload = () => renderInvisibleWidget();
      document.head.appendChild(script);
    } else if ((window as any).turnstile) {
      renderInvisibleWidget();
    } else {
      existingScript.addEventListener("load", renderInvisibleWidget);
    }
  }, [turnstileSiteKey, turnstileContainerId]);

  const runInvisibleTurnstile = async () => {
    if (!turnstileSiteKey) return "";
    const api = (window as any).turnstile;
    if (!api) throw new Error("No se pudo iniciar la verificación anti-bot.");
    // Evita reutilizar el token anterior (warning de Turnstile) al reintentar.
    if (turnstileWidgetId) {
      try { api.reset(turnstileWidgetId); } catch { /* noop */ }
      return api.execute(turnstileWidgetId, { async: true }) as Promise<string>;
    }
    return api.execute(`#${turnstileContainerId}`, { async: true }) as Promise<string>;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!email.trim()) { toast.error("Introduce un email"); return; }
    if (turnstileSiteKey && !turnstileReady) {
      toast.error("La verificación anti-bot aún no está lista. Inténtalo en unos segundos.");
      return;
    }
    setSubmitting(true);
    try {
      const turnstileToken = await runInvisibleTurnstile();
      const invokePromise = supabase.functions.invoke("send-newsletter-signup", {
        body: {
          email: email.trim().toLowerCase(),
          name: name.trim() || null,
          source,
          turnstileToken: turnstileToken || null,
        },
      });
      const timeoutPromise = new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error("Tiempo de espera agotado. Prueba de nuevo.")), FUNCTION_TIMEOUT_MS)
      );
      const { error } = await Promise.race([invokePromise, timeoutPromise]) as Awaited<typeof invokePromise>;
      if (error) throw error;
      toast.success("Suscripción completada");
      setEmail(""); setName("");
    } catch (err: any) {
      toast.error(err?.code === "23505" ? "Ese email ya está suscrito" : toSpanishError(err));
    } finally { setSubmitting(false); }
  };

  const inputCls = "w-full rounded-lg border border-border bg-background px-3 py-2.5 text-sm text-foreground outline-none transition-colors focus:border-primary/50";

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      <div className="grid gap-3 sm:grid-cols-[0.8fr_1.2fr]">
        <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Tu nombre" className={inputCls} />
        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="tu@email.com" className={inputCls} required />
      </div>
      {turnstileSiteKey && (
        <div id={turnstileContainerId} className="fixed -left-[9999px] top-0 opacity-0 pointer-events-none" aria-hidden="true" />
      )}
      <p className="-mt-4 text-[11px] text-muted-foreground">
        Al suscribirte aceptas recibir emails de Popistant. Puedes darte de baja en cualquier momento.
      </p>
      <button type="submit" disabled={submitting} className="rounded-lg bg-foreground px-4 py-2.5 text-sm font-medium text-background transition-opacity hover:opacity-90 disabled:opacity-50">
        {submitting ? "Suscribiendo..." : "Suscribirme"}
      </button>
    </form>
  );
};

export default NewsletterSignup;
